import { ChangeDetectorRef, Component, ElementRef, Inject, Injector, KeyValueDiffers, ViewChild } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import { filter, Observable, switchMap, take, tap } from 'rxjs';
import { ExportingFormats } from '@amcharts/amcharts5/.internal/plugins/exporting/Exporting';

import { ConfigService } from '../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';

import { AbstractPageBuilderService } from '../../../../Common/PageBuilder/services/abstract-page-builder.service';
import { Am5Chart } from '../../../../Common/Charts/models/am5-chart.model';
import { Am5ChartComponent } from '../../../../Common/Charts/components/Am5Chart/am5-chart.component';
import { Am5ChartConfiguration } from '../../../../Common/Charts/models/am5-chart-configuration.model';
import { Am5ChartConfigurationService } from '../../../../Common/Reports/services/am5-chart-configuration.service';
import { Am5ChartTooltipService } from '../../../../Common/Charts/services/am5-chart-tooltip.service';
import { Am5DateValueXYChart } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart.model';
import { Am5DateValueXYChartConfiguration } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-configuration.model';
import { Am5DateValueXYChartSettings } from '../../../../Common/Charts/models/Am5DateValueXYChart/am5-date-value-x-y-chart-settings.model';
import {
    reportsBlockTypesToAnnotationAdjustMap, reportsBlockTypesToInDialogAnnotationAdjustMap
} from '../../../../Common/Reports/maps/block-types-to-annotation-adjust.map';
import { ChartAnnotationDialogComponent } from '../../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-dialog.component';
import { ChartAnnotations } from '../../../../Common/Reports/models/chart-annotations.model';
import { ChartAnnotationMarkerTypesEnum } from '../../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ChartDisplayElementModel } from '../../../../Common/Reports/models/chart-display-element.model';
import { ChartExportEventModel } from '../../../../Common/Charts/models/chart-export-event.model';
import { Am5ChartSettingsService } from '../../services/am5-chart-settings.service';
import { ChartSizeHelper } from '../../../../Common/Charts/helpers/chart-size.helper';
import { ClickOutsideService } from '../../../../Common/PageBuilder/services/click-outside.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { ReportBlockTypeEnum, reportChartInfoMap } from '../../../../Common/Reports/enums/report-block-type.enum';
import { ReportsChartDetailService } from '../../../../Common/Reports/services/reports-chart-detail.service';
import { ReportsChartOptionsService } from '../../../../Common/Reports/services/reports-chart-options.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';
import { ReportsPageBuilderService } from '../../../../Common/Reports/services/reports-page-builder.service';
import { ReportsSeriesPreviewService } from '../../../../Common/Reports/services/report-series-preview.service';
import { annotatableBlockTypes } from '@Common/Reports/models/new-data-processing-workflow-block-types.model';
import { chartColors } from '@Common/Charts/enums/chart-colors.enum';
import { Am5ChartSettings } from '@Common/Charts/models/am5-chart-settings.model';
import { Am5CategoryValueXYBarChart } from '@Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYBarChart/am5-category-value-x-y-bar-chart.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { Am5CategoryValueXYColumnChart } from '@Common/Charts/models/Am5CategoryValueXYChart/Am5CategoryValueXYColumnChart/am5-category-value-x-y-column-chart.model';
import { ChartMetricsEnum } from '@Common/Reports/enums/chart-metrics.enum';
import { Am5CategoryValueXYBaseChartConfiguration } from '@Common/Charts/models/Am5CategoryValueXYChart/am5-category-value-x-y-base-chart-configuration.model';


@Component({
    selector: 'agpr-am5-chart-block',
    templateUrl: './am5-chart-block.component.html',
    styleUrls: ['../ChartBlock/chart-block.component.scss', './am5-chart-block.component.scss'],
    providers: [
        Am5ChartTooltipService
    ],
    standalone: false
})
export class Am5ChartBlockComponent extends ChartBlockComponent {

    am5Chart: Am5Chart;
    currency: string;

    plotTop: number = 0;
    plotBottom: number = 0;
    plotLeft: number = 15;
    annotationTopAdjust: number = 0;
    annotationBottomAdjust: number = 0;
    annotationLeftAdjust: number = 0;
    inDialogAnnotationTopAdjust: number = 0;
    inDialogAnnotationBottomAdjust: number = 0;

    readonly supportedExportingFormats: ExportingFormats[] = [
        'png',
        'jpg',
        'csv',
        'json',
        'print'
    ];

    @ViewChild('am5ChartComponent') am5ChartComponent: Am5ChartComponent;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public elementRef: ElementRef,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService,
        public am5ChartConfigurationService: Am5ChartConfigurationService,
        public am5ChartSettingsService: Am5ChartSettingsService,
        public am5ChartTooltipService: Am5ChartTooltipService,
        public coreDialogService: CoreDialogService,
        public reportsPageBuilderService: ReportsPageBuilderService,
        public cd: ChangeDetectorRef,
        private injector: Injector
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );

        this.subscriptions.push(
            this.userStateService.getUserCurrency().subscribe(currency => this.currency = currency),
            this.reportsPageBuilderService.getResizedBlock().subscribe(x => {
                if (!this.block || !x) {
                    return;
                }
                const currentBlock = this.block.clone();
                currentBlock.rows = 0;
                currentBlock.cols = 0;
                const resizedBlock = x.clone();
                resizedBlock.rows = 0;
                resizedBlock.cols = 0;
                if (currentBlock.equals(resizedBlock)) {
                    this.am5Chart.settings.rows = x.rows;
                    this.am5Chart.settings.cols = x.cols;
                    this.am5Chart.settings.width = ChartSizeHelper.getChartGridWidth(x.cols);
                    this.am5Chart.settings.height = ChartSizeHelper.getChartGridWidth(x.rows);
                }
                this.setPlotPosition();
            })
        );
    }

    openDrilldown(event: any): void {
        const url = event.data;
        if (url) {
            // we would like to pass the chart instead of the block for drilldown.. if we pass block.. if you re-drilldown,
            // it will close and re-open the dialog, because this gets ran again since it's a block component
            if ((this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock &&
                ((this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock(this.block) as Am5ChartBlockComponent)?.am5Chart
            ) {
                const chart = (
                    (this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock(this.block) as Am5ChartBlockComponent
                )?.am5Chart.clone();
                if (chart) {
                    chart.settings.chartAnnotations = new ChartAnnotations();
                }
                this.reportsChartDetailService.viewCoverageAnalysisDialog(
                    url,
                    null,
                    chart,
                    false,
                    null,
                    null,
                    this.reportSeries.map(x => x.clone())
                );
            } else {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(url, this.block);
            }
        }
    }

    constructChart(series: ReportSeriesModel[]): void {
        const displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (!displayElement.chartAnnotations) {
            displayElement.chartAnnotations = new ChartAnnotations();
        }
        // Only chart types that support annotations have entries in this map; others fall back to [0, 0].
        [this.annotationTopAdjust, this.annotationBottomAdjust, this.annotationLeftAdjust] =
            reportsBlockTypesToAnnotationAdjustMap.get(this.block.type.key as ReportBlockTypeEnum) ?? [0, 0, 0];
        [this.inDialogAnnotationTopAdjust, this.inDialogAnnotationBottomAdjust] =
            reportsBlockTypesToInDialogAnnotationAdjustMap.get(this.block.type.key as ReportBlockTypeEnum) ?? [0, 0];
        const blockType = this.block.type.key as ReportBlockTypeEnum;
        const chartSeries = this.reportsChartSeriesConverterService.getChartSeries(series, this.block);
        const chartConfig: Am5DateValueXYChartConfiguration = this.am5ChartConfigurationService.getChartConfiguration(
            this.id, blockType, displayElement
        );
        const chartSettings: Am5DateValueXYChartSettings = this.am5ChartSettingsService.getChartSettings(
            blockType, displayElement, this.block.rows, this.block.cols
        );
        chartSettings.currencyCode = this.currency;
        chartSeries.forEach((x, i) => {
            x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfig, chartSettings, i, this.preview);
        });
        chartSettings.colorMap = { ...this.displayElement.styles.colorMap };
        chartSettings.isSingleSeries = series.length === 1;
        switch (this.block.type.key) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                this.am5Chart = new Am5DateValueXYChart(chartSeries, chartConfig, chartSettings) as unknown as Am5Chart;
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                chartSeries.forEach((cs, i) => {
                    if (series[i]?.color && cs.colorMapKey) {
                        chartSettings.colorMap[cs.colorMapKey] = series[i].color;
                    }
                });
                this.am5Chart = new Am5DateValueXYChart(chartSeries, chartConfig, chartSettings) as unknown as Am5Chart;
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                chartSeries.forEach((x, i) => {
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip(chartSeries, chartConfig, chartSettings, i, this.preview);
                });
                this.am5Chart = new Am5DateValueXYChart(chartSeries, chartConfig, chartSettings) as unknown as Am5Chart;
                break;
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                chartConfig.legendItemWidth = 150;
                chartConfig.legendMaxColumns = 2;
                chartConfig.centerLegend = true;
                // impact trend has two sources (mentions + external traffic); show the traffic source link too
                if (displayElement.series.length > 1) {
                    chartConfig.header.secondarySeriesLabelText = displayElement.series[1].label;
                }
                chartSeries.forEach((x, i) => {
                    // only the mentions series (index 0) drills down to coverage analysis, the
                    // external traffic series have no mention drilldown
                    this.am5ChartTooltipService.setDoLink(i === 0);
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfig, chartSettings, i, this.preview);
                });
                this.am5Chart = new Am5DateValueXYChart(chartSeries, chartConfig, chartSettings) as unknown as Am5Chart;
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                if (displayElement.metric === ChartMetricsEnum.domainAuthority) {
                    (chartConfig as Am5CategoryValueXYBaseChartConfiguration).noPercentage = true;
                }
                if (this.isBarChart(chartSettings)) {
                    this.am5Chart = new Am5CategoryValueXYBarChart(
                        chartSeries,
                        chartConfig,
                        chartSettings
                    );
                } else {
                    this.am5Chart = new Am5CategoryValueXYColumnChart(
                        chartSeries,
                        chartConfig,
                        chartSettings
                    );
                }
                break;
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                if (!chartSettings.colorMap['barColor']) {
                    chartSettings.colorMap['barColor'] = chartSettings.colorMap['seriesColor0'] || chartColors.agprBlue;
                }
                // google analytics data has no mention drilldown
                this.am5ChartTooltipService.setDoLink(false);
                chartSeries.forEach((x, i) => {
                    x.tooltipHTML = this.am5ChartTooltipService.getChartTooltip([x], chartConfig, chartSettings, i, this.preview);
                });
                if (this.isBarChart(chartSettings)) {
                    this.am5Chart = new Am5CategoryValueXYBarChart(
                        chartSeries,
                        chartConfig,
                        chartSettings
                    );
                } else {
                    this.am5Chart = new Am5CategoryValueXYColumnChart(
                        chartSeries,
                        chartConfig,
                        chartSettings
                    );
                }
                break;
        }
        this.initializeAnnotations(blockType, chartSettings);
    }

    initializeAnnotations(blockType: ReportBlockTypeEnum, chartSettings: Am5ChartSettings): void {
        if (annotatableBlockTypes.includes(blockType)) {
            this.am5Chart.annotatorConfigured$.pipe(
                filter(x => !!x),
                switchMap(() => new Observable<void>(observer => {
                    this.am5Chart.root.events.once('frameended', () => {
                        observer.next();
                        observer.complete();
                    });
                })),
                switchMap(async () => {
                    this.setPlotPosition();
                    return this.am5Chart.annotator ? await this.am5Chart.annotator.getMarkerArea() : null;
                }),
                filter(x => !!x),
                take(1)
            ).subscribe(area => {
                area.availableMarkerTypes = Object.values(ChartAnnotationMarkerTypesEnum);
                this.am5ChartComponent.updateChartAnnotations(
                    chartSettings.chartAnnotations?.markerState,
                    chartSettings.chartAnnotations?.indicators ?? []
                );
            });
        }
    }

    formatDateRangeLabel(format: string) {
        this.am5Chart.dateRangeLabel?.set('text', this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format));
    }

    public chartExport(event: ChartExportEventModel) {
        this.am5Chart.export(event.eventKey.toLowerCase(), this.displayElement.title);
    }

    /**
     * Gets the export image for the full report pdf export
     * @NOTE don't remove the width/height params.
     */
    public async getFullExportImage(
        width: number,
        height: number,
        transparent: boolean = false
    ): Promise<string> {
        if (!this.am5Chart) {
            return null;
        }

        const annotator = this.am5Chart.annotator;
        const preExportGeometry = annotator ? {
            containerWidth: annotator.containerWidth,
            containerHeight: annotator.containerHeight,
            plotTop: annotator.plotTop,
            plotBottom: annotator.plotBottom,
            plotLeft: annotator.plotLeft,
            plotRight: annotator.plotRight
        } : null;

        return await this.am5Chart.getPNGImageData(
            undefined,
            undefined,
            transparent,
            false,
            (exportWidth, exportHeight) => {
                this.setPlotPosition(exportWidth, exportHeight);
                annotator?.applyContainerGeometry();
            },
            () => {
                if (annotator && preExportGeometry?.containerWidth && preExportGeometry?.containerHeight) {
                    annotator.setContainerWidth(preExportGeometry.containerWidth);
                    annotator.setContainerHeight(preExportGeometry.containerHeight);
                    annotator.setPlotTop(preExportGeometry.plotTop);
                    annotator.setPlotBottom(preExportGeometry.plotBottom);
                    annotator.setPlotLeft(preExportGeometry.plotLeft);
                    annotator.setPlotRight(preExportGeometry.plotRight);
                    annotator.applyContainerGeometry();
                }
            }
        );
    }

    onAction(action: string): void {
        super.onAction(action);
        if (action === 'annotate') {
            this.enterAnnotationMode();
        }
    }

    enterAnnotationMode(): void {
        const chart = this.am5Chart.clone();
        this.updateConfigurationForAnnotationDialog(chart.config);
        chart.series = chart.series.filter(x => !x.indicatorTargetIndex && x.indicatorTargetIndex !== 0);
        const dialogInjector = Injector.create({
            providers: [{ provide: AbstractPageBuilderService, useValue: this.pageBuilderService }],
            parent: this.injector
        });
        this.coreDialogService.open(
            ChartAnnotationDialogComponent,
            {
                inputs: {
                    chart: chart,
                    reportSeries: this.reportSeries,
                    displayElement: this.displayElement,
                    plotTop: this.plotTop,
                    plotBottom: this.plotBottom,
                    chartContainerWidth: chart.settings.width
                },
                outputs:{
                    dialogClose: (chartAnnotations: ChartAnnotations) => {
                        if (!chartAnnotations) {
                            return;
                        }
                        const displayElement = this.block.displayElement as ChartDisplayElementModel;
                        const markerState = chartAnnotations.markerState || displayElement.chartAnnotations.markerState;
                        displayElement.chartAnnotations.markerState = markerState;
                        displayElement.chartAnnotations.indicators = chartAnnotations.indicators;
                        this.am5ChartComponent.updateChartAnnotations(markerState, chartAnnotations.indicators);
                    }
                }
            },
            dialogInjector
        );
    }

    setPlotPosition(overrideWidth?: number, overrideHeight?: number): void {
        if (!this.am5Chart.annotator) {
            return;
        }
        let width = overrideWidth ?? this.am5Chart.settings.width;
        let height = overrideHeight ?? this.am5Chart.settings.height;
        if (!overrideWidth && this.preview) {
            const type = this.am5Chart.config.type as ReportBlockTypeEnum;
            let cols = reportChartInfoMap.get(type)?.dimensions.width;
            const rows = reportChartInfoMap.get(type)?.dimensions.height;

            if (type === ReportBlockTypeEnum.TOP_10_OUTLETS || type === ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME) {
                if (this.displayElement.chartHeaderSettings.entityProfile) {
                    cols = 5;
                }
            }
            width = ChartSizeHelper.getChartGridWidth(cols);
            height = ChartSizeHelper.getChartGridHeight(rows);
        }
        this.am5Chart.annotator.setContainerWidth(width);
        this.am5Chart.annotator.setContainerHeight(height);
        const plotContainer = (this.am5Chart as Am5DateValueXYChart).amchart.plotContainer;
        this.plotTop = plotContainer.toGlobal({ x: 0, y: 0 }).y + this.annotationTopAdjust;
        if (this.am5Chart.legend) {
            this.plotBottom = this.am5Chart.legend.toGlobal({ x: 0, y: 0 }).y + this.annotationBottomAdjust;
        } else {
            this.plotBottom = (height ?? 0) + this.annotationBottomAdjust;
        }
        this.am5Chart.annotator.setPlotTop(this.plotTop);
        this.am5Chart.annotator.setPlotBottom(this.plotBottom);
        this.am5Chart.annotator.setPlotLeft(15);
        this.am5Chart.annotator.setPlotRight(15);
    }

    updateConfigurationForAnnotationDialog(config: Am5ChartConfiguration): void {
        config.padding.customPadding = this.getCustomPaddingForAnnotationDialog(config.type);
        config.header.hasHeader = false;
        config.hasLegend = false;
        config.disableLegendClick = true;
        switch (config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                (config as Am5DateValueXYChartConfiguration).useScrollbar = false;
                break;
        }
    }

    getCustomPaddingForAnnotationDialog(type: ReportBlockTypeEnum): any {
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            default:
                return {
                    top: -this.inDialogAnnotationTopAdjust,
                    right: 0,
                    bottom: this.inDialogAnnotationBottomAdjust,
                    left: 0
                };
        }
    }

    public isBarChart(settings: Am5ChartSettings): boolean {
        return settings.chartSpecificType === ChartSpecificTypesEnum.BAR ||
            settings.chartSpecificType === ChartSpecificTypesEnum.MULTI_STACKED_BAR;
    }
}
